import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Database, Download, Upload, Trash2, RefreshCw,
  CheckCircle2, AlertCircle, HardDrive
} from 'lucide-react';
import { BottomSheet } from './BottomSheet';
import taskDatabase from '../../services/taskDatabase';

/**
 * DataManagementSheet - Gestion des données (export, import, reset)
 */
export function DataManagementSheet({ isOpen, onClose }) {
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [status, setStatus] = useState(null);
  const [stats, setStats] = useState(null);

  // Load database stats
  const loadStats = useCallback(async () => {
    try {
      await taskDatabase.init();
      const data = await taskDatabase.exportAllExtended();

      setStats({
        tasks: data.tasks?.length || 0,
        completed: data.completed?.length || 0,
        templates: data.templates?.length || 0,
        customTemplates: data.customTemplates?.length || 0,
        proofs: data.proofs?.length || 0,
        moodEntries: data.moodTracking?.length || 0,
        lastExport: localStorage.getItem('adhd_last_export'),
      });
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, []);

  // Load stats when sheet opens
  React.useEffect(() => {
    if (isOpen) {
      loadStats();
    }
  }, [isOpen, loadStats]);

  // Export all data
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    setStatus(null);

    try {
      const data = await taskDatabase.exportAllExtended();

      // Create downloadable file
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const date = new Date().toISOString().split('T')[0];
      link.href = url;
      link.download = `adhd-planner-backup-${date}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      localStorage.setItem('adhd_last_export', new Date().toISOString());

      setStatus({ type: 'success', message: 'Données exportées avec succès !' });
      loadStats();
    } catch (err) {
      console.error('Export error:', err);
      setStatus({ type: 'error', message: 'Erreur lors de l\'export' });
    } finally {
      setIsExporting(false);
    }
  }, [loadStats]);

  // Import data
  const handleImport = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setStatus(null);

    try {
      const text = await file.text();
      const data = JSON.parse(text);

      // Validate data structure
      if (!data.version || !data.exportedAt) {
        throw new Error('Fichier invalide');
      }

      await taskDatabase.importAll(data);

      setStatus({
        type: 'success',
        message: `Import réussi ! ${data.tasks?.length || 0} tâches restaurées.`
      });
      loadStats();
    } catch (err) {
      console.error('Import error:', err);
      setStatus({
        type: 'error',
        message: err.message === 'Fichier invalide'
          ? 'Fichier de sauvegarde invalide'
          : 'Erreur lors de l\'import'
      });
    } finally {
      setIsImporting(false);
      // Reset file input
      event.target.value = '';
    }
  }, [loadStats]);

  // Clear all data
  const handleClearAll = useCallback(async () => {
    if (!window.confirm('⚠️ Supprimer TOUTES les données ? Cette action est irréversible !')) {
      return;
    }

    if (!window.confirm('Es-tu vraiment sûr(e) ? Toutes tes tâches, templates et statistiques seront perdus.')) {
      return;
    }

    try {
      // Clear IndexedDB
      const stores = [
        'tasks', 'completed', 'templates', 'proofs', 'syncQueue',
        'actionHistory', 'settings', 'customTemplates', 'autoGeneration',
        'userSchedule', 'gamification', 'moodTracking', 'weeklyStats'
      ];

      for (const store of stores) {
        try {
          await taskDatabase.clear(store);
        } catch {}
      }

      // Clear localStorage
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('adhd')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      setStatus({ type: 'success', message: 'Toutes les données ont été supprimées' });
      loadStats();
    } catch (err) {
      console.error('Clear error:', err);
      setStatus({ type: 'error', message: 'Erreur lors de la suppression' });
    }
  }, [loadStats]);

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Gestion des données"
      size="large"
    >
      <div className="p-4 space-y-5 max-h-[70vh] overflow-y-auto">
        {/* Stats */}
        {stats && (
          <div className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Database className="w-4 h-4 text-zinc-400" />
              <span className="text-sm font-medium text-zinc-300">Statistiques</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <StatBox label="Tâches" value={stats.tasks} />
              <StatBox label="Complétées" value={stats.completed} />
              <StatBox label="Templates" value={stats.customTemplates} />
              <StatBox label="Preuves" value={stats.proofs} />
              <StatBox label="Humeurs" value={stats.moodEntries} />
              <StatBox
                label="Dernier export"
                value={stats.lastExport
                  ? new Date(stats.lastExport).toLocaleDateString('fr-FR')
                  : '-'
                }
                small
              />
            </div>
          </div>
        )}

        {/* Status message */}
        {status && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex items-center gap-2 p-3 rounded-lg ${
              status.type === 'success'
                ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
                : 'bg-red-500/10 border border-red-500/30 text-red-400'
            }`}
          >
            {status.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4" />
            ) : (
              <AlertCircle className="w-4 h-4" />
            )}
            <span className="text-sm">{status.message}</span>
          </motion.div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {/* Export */}
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-cyan-950/30 to-blue-950/30 border border-cyan-500/30 hover:border-cyan-500/50 rounded-xl transition-all disabled:opacity-50"
          >
            <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
              {isExporting ? (
                <RefreshCw className="w-5 h-5 text-cyan-400 animate-spin" />
              ) : (
                <Download className="w-5 h-5 text-cyan-400" />
              )}
            </div>
            <div className="text-left flex-1">
              <span className="text-sm font-medium text-cyan-200 block">
                {isExporting ? 'Export en cours...' : 'Exporter mes données'}
              </span>
              <span className="text-xs text-cyan-400/70">
                Télécharger une sauvegarde JSON
              </span>
            </div>
          </button>

          {/* Import */}
          <label className="w-full flex items-center gap-3 p-4 bg-zinc-800/30 border border-zinc-700/50 hover:border-zinc-600 rounded-xl transition-all cursor-pointer">
            <div className="w-10 h-10 bg-zinc-700/50 rounded-lg flex items-center justify-center">
              {isImporting ? (
                <RefreshCw className="w-5 h-5 text-zinc-400 animate-spin" />
              ) : (
                <Upload className="w-5 h-5 text-zinc-400" />
              )}
            </div>
            <div className="text-left flex-1">
              <span className="text-sm font-medium text-zinc-200 block">
                {isImporting ? 'Import en cours...' : 'Importer une sauvegarde'}
              </span>
              <span className="text-xs text-zinc-500">
                Restaurer depuis un fichier JSON
              </span>
            </div>
            <input
              type="file"
              accept=".json"
              onChange={handleImport}
              disabled={isImporting}
              className="hidden"
            />
          </label>

          {/* Sync info */}
          <div className="flex items-center gap-3 p-4 bg-zinc-800/20 border border-zinc-800 rounded-xl">
            <div className="w-10 h-10 bg-zinc-800 rounded-lg flex items-center justify-center">
              <HardDrive className="w-5 h-5 text-zinc-500" />
            </div>
            <div className="text-left flex-1">
              <span className="text-sm font-medium text-zinc-400 block">Stockage local</span>
              <span className="text-xs text-zinc-600">
                Données stockées sur cet appareil (IndexedDB)
              </span>
            </div>
          </div>

          {/* Clear all */}
          <button
            onClick={handleClearAll}
            className="w-full flex items-center gap-3 p-4 bg-red-950/20 border border-red-900/30 hover:border-red-500/50 rounded-xl transition-all group"
          >
            <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center group-hover:bg-red-500/20 transition-colors">
              <Trash2 className="w-5 h-5 text-red-400" />
            </div>
            <div className="text-left flex-1">
              <span className="text-sm font-medium text-red-300 block">
                Supprimer toutes les données
              </span>
              <span className="text-xs text-red-400/60">
                Action irréversible
              </span>
            </div>
          </button>
        </div>

        {/* Info */}
        <div className="text-center text-xs text-zinc-600 pt-2">
          <p>Les données sont stockées localement sur ton appareil.</p>
          <p>Exporte régulièrement pour ne rien perdre !</p>
        </div>
      </div>
    </BottomSheet>
  );
}

/**
 * StatBox - Mini stat display
 */
function StatBox({ label, value, small = false }) {
  return (
    <div className="bg-zinc-900/50 rounded-lg p-2 text-center">
      <span className={`font-bold text-zinc-200 block ${small ? 'text-xs' : 'text-lg'}`}>
        {value}
      </span>
      <span className="text-[10px] text-zinc-500">{label}</span>
    </div>
  );
}

export default DataManagementSheet;
